import { useEffect, useState } from "react";

import { ValueSetExpansionContains } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { NativeSelect, Stack, Textarea, TextInput } from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";

import {
  ROM_CONFIRMED_BY_LINK_ID,
  ROM_DATE_TIME_LINK_ID,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/rom/constants/rom-link-ids";
import type { RomFlowsheetDrawerMode } from "@/encounters/components/EncounterLabor/flowsheet-forms/rom/types/rom-drawer";
import type { RomFlowsheetFormValues } from "@/encounters/components/EncounterLabor/flowsheet-forms/rom/types/rom-form";
import {
  isOtherNativeSelectValue,
  valueSetContainsToNativeSelectOptions,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/labor-flowsheet-coding-select";
import { LaborFlowsheetDateTimeFields } from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/LaborFlowsheetDateTimeFields";
import { LaborFlowsheetSignatureInitialsField } from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/LaborFlowsheetSignatureInitialsField";
import { QuiltedHealthValueSets } from "@/fhir/enums/quilted-health";
import { questionnaireFieldTestId } from "@/intake-forms/utils/questionnaire-field-testid";
import { getValueSetOptions } from "@/utils/medplum";
import { toast } from "@/utils/notifications";

interface RomFormFieldsProps {
  form: UseFormReturnType<RomFlowsheetFormValues>;
  clientId: string;
  mode?: RomFlowsheetDrawerMode;
  isDrawerOpened?: boolean;
}

export function RomFormFields({
  form,
  clientId,
  mode = "quick-add",
  isDrawerOpened = true,
}: RomFormFieldsProps) {
  const medplum = useMedplum();
  const [methodOptions, setMethodOptions] = useState<ValueSetExpansionContains[]>([]);
  const [confirmedByOptions, setConfirmedByOptions] = useState<ValueSetExpansionContains[]>([]);
  const [fluidColorOptions, setFluidColorOptions] = useState<ValueSetExpansionContains[]>([]);
  const showConfirmedByOther = isOtherNativeSelectValue(form.values.confirmedBy);
  const showFluidColorOther = isOtherNativeSelectValue(form.values.fluidColor);
  const methodInputProps = form.getInputProps("method");
  const confirmedByInputProps = form.getInputProps("confirmedBy");
  const fluidColorInputProps = form.getInputProps("fluidColor");

  useEffect(() => {
    if (!isDrawerOpened) {
      return;
    }

    let isCancelled = false;

    const loadValueSetOptions = async () => {
      const valueSetRequests = [
        {
          label: "Method",
          valueSet: QuiltedHealthValueSets.ROMMethod,
          setOptions: setMethodOptions,
        },
        {
          label: "confirmed by",
          valueSet: QuiltedHealthValueSets.ROMConfirmedBy,
          setOptions: setConfirmedByOptions,
        },
        {
          label: "fluid color",
          valueSet: QuiltedHealthValueSets.MembraneFluidColor,
          setOptions: setFluidColorOptions,
        },
      ] as const;

      const results = await Promise.allSettled(
        valueSetRequests.map(({ valueSet }) => getValueSetOptions(medplum, valueSet)),
      );

      if (isCancelled) {
        return;
      }

      const failedLabels: string[] = [];

      results.forEach((result, index) => {
        const { label, setOptions } = valueSetRequests[index];

        if (result.status === "fulfilled") {
          setOptions(result.value ?? []);
          return;
        }

        console.error(`Error loading ROM ${label} value set:`, result.reason);
        failedLabels.push(label);
      });

      if (failedLabels.length > 0) {
        const failedOptionsLabel =
          failedLabels.length === valueSetRequests.length
            ? "Method, confirmed by, and fluid color options"
            : `${failedLabels.join(", ")} options`;

        toast({
          type: "error",
          title: "Failed to load form options",
          message: `${failedOptionsLabel} could not be loaded. Try closing and reopening the drawer.`,
          autoClose: false,
        });
      }
    };

    void loadValueSetOptions();

    return () => {
      isCancelled = true;
    };
  }, [isDrawerOpened, medplum]);

  return (
    <Stack gap="lg">
      <LaborFlowsheetDateTimeFields
        form={form}
        dateFieldName="examDate"
        timeFieldName="examTime"
        autoPopulateDateTime={mode === "quick-add"}
        dateTimeGroupLinkId={ROM_DATE_TIME_LINK_ID}
      />

      <NativeSelect
        label="Method"
        data-path="romMethod"
        data={valueSetContainsToNativeSelectOptions(methodOptions)}
        {...methodInputProps}
        value={methodInputProps.value ?? ""}
        data-testid={questionnaireFieldTestId("romMethod")}
      />

      <NativeSelect
        label="Confirmed By"
        data-path={ROM_CONFIRMED_BY_LINK_ID}
        data={valueSetContainsToNativeSelectOptions(confirmedByOptions)}
        {...confirmedByInputProps}
        value={confirmedByInputProps.value ?? ""}
        data-testid={questionnaireFieldTestId("romConfirmedBy")}
      />

      {showConfirmedByOther ? (
        <TextInput
          label="Confirmed By Other"
          data-path="romConfirmedByOther"
          {...form.getInputProps("confirmedByOther")}
          data-testid={questionnaireFieldTestId("romConfirmedByOther")}
        />
      ) : null}

      <NativeSelect
        label="Fluid Color"
        data-path="romFluidColor"
        data={valueSetContainsToNativeSelectOptions(fluidColorOptions)}
        {...fluidColorInputProps}
        value={fluidColorInputProps.value ?? ""}
        data-testid={questionnaireFieldTestId("romFluidColor")}
      />

      {showFluidColorOther ? (
        <TextInput
          label="Fluid Color Other"
          data-path="romFluidColorOther"
          {...form.getInputProps("fluidColorOther")}
          data-testid={questionnaireFieldTestId("romFluidColorOther")}
        />
      ) : null}

      <Textarea
        label="Notes"
        data-path="romNotes"
        {...form.getInputProps("notes")}
        data-testid={questionnaireFieldTestId("romNotes")}
      />

      <LaborFlowsheetSignatureInitialsField
        form={form}
        clientId={clientId}
        label="Sign Initials"
        name="signatureInitials"
        autoPopulateSignature={mode === "quick-add"}
      />
    </Stack>
  );
}
